import type { CSSProperties, ReactNode } from 'react';
import { EnvelopeSimpleIcon } from '@phosphor-icons/react';
import { getCampaignApiUrl } from '@/lib/api/campaign-api-client';
import type { PublicBranding } from '../types/public-consent.types';

const PLATFORM_ACCENT = '#0066B2';

type PublicConsentPageProps = {
  branding?: PublicBranding;
  children: ReactNode;
};

export const brandName = (branding?: PublicBranding): string =>
  branding?.resolvedName || branding?.displayName || 'Our newsletter';

export const brandAccent = (branding?: PublicBranding): string =>
  branding?.accentColor || PLATFORM_ACCENT;

const logoSource = (logoUrl?: string | null): string => {
  if (!logoUrl) return '';
  return /^https?:\/\//i.test(logoUrl) ? logoUrl : getCampaignApiUrl(logoUrl);
};

export function PublicConsentPage({ branding, children }: PublicConsentPageProps) {
  const name = brandName(branding);
  const source = logoSource(branding?.logoUrl);
  const style = { '--public-accent': brandAccent(branding) } as CSSProperties;

  return (
    <main className="public-consent-page" style={style}>
      <section className="public-consent-shell" aria-label={`${name} consent page`}>
        <div className="public-consent-card">
          <header className="public-consent-brand">
            {source ? (
              <img className="public-consent-brand__logo" src={source} alt={`${name} logo`} />
            ) : (
              <span className="public-consent-brand__mark" aria-hidden="true">
                <EnvelopeSimpleIcon size={24} weight="duotone" />
              </span>
            )}
            <span className="public-consent-brand__name">{name}</span>
          </header>
          {children}
        </div>
        {branding?.showPoweredBy === true ? (
          <p className="public-consent-powered">Powered by SELISE Campaign</p>
        ) : null}
      </section>
    </main>
  );
}

type PublicConsentButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export function PublicConsentButton({ children, style, ...props }: PublicConsentButtonProps) {
  return (
    <button
      type="button"
      className="public-consent-button"
      style={{ backgroundColor: 'var(--public-accent)', ...style }}
      {...props}
    >
      {children}
    </button>
  );
}
